import React, { forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MaterialTable from 'material-table';
import { Link } from 'react-router-dom';

import { AuthorizeContext } from '../../RootContext';
import { api_url } from '../../authorize';
import logo from './../../img/lento_logo_transparent.png';

import {
  createMuiTheme,
  makeStyles,
  ThemeProvider,
} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  Reset: forwardRef((props, ref) => <RotateLeftIcon {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#6A58A6',
    },
    secondary: {
      main: '#3B9D9A',
    },
    background: {
      default: '#f4f7fb',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    h6: {
      fontWeight: 700,
    },
  },
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 9,
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: 9,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 14,
      },
    },
  },
});

const useStyles = makeStyles((muiTheme) => ({
  page: {
    minHeight: 'calc(100vh - 72px)',
    padding: '18px 8px 36px',
    boxSizing: 'border-box',
    background: 'radial-gradient(circle at 8% 2%, #EAF8F2 0, transparent 30%), radial-gradient(circle at 92% 0%, #E8F7F6 0, transparent 32%), linear-gradient(180deg, #F9FCFB 0%, #EEF7F4 100%)',
    color: '#18283a',

    [muiTheme.breakpoints.down('sm')]: {
      padding: '20px 14px 34px',
    },
  },
  shell: {
    width: '100%',
    maxWidth: 'none',
    margin: 0,
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 22,
    padding: '24px 26px',
    borderRadius: 20,
    background: 'linear-gradient(135deg, #123B5D 0%, #0D6E8A 58%, #169B97 100%)',
    border: '1px solid rgba(255, 255, 255, 0.24)',
    boxShadow: '0 18px 42px rgba(22, 155, 151, 0.18)',

    [muiTheme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: 14,
      padding: '21px 20px',
    },
  },
  eyebrow: {
    margin: 0,
    color: 'rgba(255, 255, 255, 0.82)',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  title: {
    margin: '7px 0 0',
    color: '#ffffff',
    fontSize: 32,
    lineHeight: 1.15,
    fontWeight: 800,
    letterSpacing: '-0.035em',

    [muiTheme.breakpoints.down('xs')]: {
      fontSize: 27,
    },
  },
  subtitle: {
    maxWidth: 760,
    margin: '8px 0 0',
    color: 'rgba(255, 255, 255, 0.84)',
    fontSize: 15,
    lineHeight: 1.6,
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  metaPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 38,
    padding: '0 14px',
    borderRadius: 999,
    background: 'rgba(255, 255, 255, 0.94)',
    border: '1px solid rgba(255, 255, 255, 0.72)',
    boxShadow: '0 10px 24px rgba(12, 38, 60, 0.18)',
    color: '#40366F',
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  metaDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#3B9D9A',
    boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.52)',
  },
  card: {
    overflow: 'hidden',
    borderRadius: 18,
    background: '#ffffff',
    border: '1px solid #B9DDD2',
    borderTop: '4px solid #0D6E8A',
    boxShadow: '0 20px 52px rgba(27, 154, 146, 0.16)',

    '& .MuiPaper-root': {
      boxShadow: 'none',
      borderRadius: 0,
    },
    '& .MuiToolbar-root': {
      minHeight: 68,
      paddingLeft: 22,
      paddingRight: 18,
      borderBottom: '1px solid #B9DDD2',
      background: 'linear-gradient(90deg, #FFFFFF 0%, #E8F7F6 100%)',
    },
    '& .MuiTable-root': {
      width: '100%',
      minWidth: 0,
      tableLayout: 'auto',
    },
    '& .MuiTableCell-root': {
      boxSizing: 'border-box',
    },
    '& .MuiTableCell-head': {
      padding: '12px 8px',
      background: '#E8F7F6',
      borderTop: '1px solid #D7CFEC',
      borderBottom: '1px solid #D7CFEC',
      color: '#194F47',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
      fontSize: 12.5,
      fontWeight: 700,
      lineHeight: 1.35,
      letterSpacing: '0.01em',
      whiteSpace: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordBreak: 'normal',
    },
    '& thead .MuiTableRow-root:first-child .MuiTableCell-head': {
      background: 'linear-gradient(135deg, #17324D 0%, #114E73 52%, #0D6E8A 100%)',
      color: '#ffffff',
      borderColor: 'rgba(255, 255, 255, 0.14)',
    },
    '& thead .MuiTableRow-root:nth-child(2) .MuiTableCell-head': {
      background: 'linear-gradient(90deg, #EAF8F2 0%, #E8F7F6 100%)',
      color: '#194F47',
      borderBottom: '1px solid #B9DDD2',
    },
    '& .MuiTableCell-body': {
      padding: '11px 8px',
      borderBottom: '1px solid #edf1f5',
      color: '#263b50',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
      fontSize: 12.5,
      fontWeight: 500,
      lineHeight: 1.4,
      verticalAlign: 'middle',
      whiteSpace: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      overflowWrap: 'anywhere',
    },
    '& .MuiTableCell-head:last-child': {
      paddingLeft: 2,
      paddingRight: 2,
      textAlign: 'center',
    },
    '& .MuiTableCell-body:last-child': {
      paddingLeft: 2,
      paddingRight: 2,
      textAlign: 'center',
    },
    '& .MuiTableCell-body:last-child > div': {
      width: '100%',
      display: 'flex !important',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
    },
    '& .MuiTableCell-body:last-child .MuiIconButton-root': {
      margin: 0,
    },
    '& .MuiTableCell-paddingCheckbox': {
      paddingLeft: 4,
      paddingRight: 4,
    },
    '& .MuiIconButton-root': {
      padding: 5,
      color: '#49647d',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
    '& .MuiTableBody-root .MuiTableRow-root': {
      transition: 'background-color 140ms ease, box-shadow 140ms ease',
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
      backgroundColor: '#F8FCFA',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: '#E8F7F1 !important',
      boxShadow: 'inset 4px 0 0 #0D6E8A',
    },
    '& .MuiInputBase-root': {
      color: '#263b50',
      fontSize: 14,
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#0D6E8A',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottomColor: '#6A58A6',
    },
    '& .MuiTableContainer-root': {
      width: '100%',
      overflowX: 'auto',
    },
    '& .MuiTablePagination-root': {
      borderTop: '1px solid #D7CFEC',
      background: 'linear-gradient(90deg, #FFFFFF 0%, #E8F7F6 100%)',
      color: '#194F47',
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: 62,
    },
    '& .MuiIconButton-root:hover': {
      color: '#248A73',
      background: '#EAF8F2',
    },
  },
  cardIntro: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    padding: '18px 22px',
    borderBottom: '1px solid #B9DDD2',
    background: 'linear-gradient(105deg, #EAF8F2 0%, #FFFFFF 46%, #E8F7F6 100%)',

    [muiTheme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  cardLabel: {
    margin: 0,
    color: '#194F47',
    fontSize: 15,
    fontWeight: 800,
  },
  cardHint: {
    margin: '4px 0 0',
    color: '#718396',
    fontSize: 13,
    lineHeight: 1.5,
  },
  accessBadge: {
    flex: '0 0 auto',
    padding: '7px 11px',
    borderRadius: 999,
    color: '#176B5B',
    background: 'linear-gradient(135deg, #EAF8F2 0%, #E8F7F6 100%)',
    border: '1px solid #B9DDD2',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.055em',
    textTransform: 'uppercase',
  },
  nameLink: {
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    color: '#176B5B',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    fontSize: 'inherit',
    fontWeight: 700,
    textDecoration: 'underline',
    textDecorationColor: 'rgba(23, 107, 91, 0.42)',
    textDecorationThickness: '1px',
    textUnderlineOffset: '2px',
    cursor: 'pointer',

    '&:hover': {
      color: '#0F5B4D',
      textDecorationColor: '#0F5B4D',
    },
  },
  officeCell: {
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
  },
  addressCell: {
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
  },
  emailCell: {
    display: 'inline-block',
    width: 'auto',
    minWidth: 'max-content',
    maxWidth: 'none',
    overflow: 'visible',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 0,
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
    overflowWrap: 'normal',
    wordBreak: 'normal',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    width: '100%',
    minWidth: 0,
    maxWidth: 86,
    padding: '5px 6px',
    boxSizing: 'border-box',
    borderRadius: 999,
    fontSize: 9.5,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  statusActive: {
    color: '#0D6B4B',
    background: 'linear-gradient(135deg, #DCF8EB 0%, #CFF2E3 100%)',
    border: '1px solid #A9DEC8',
    boxShadow: '0 4px 10px rgba(13, 107, 75, 0.10)',
  },
  statusInactive: {
    color: '#5C6470',
    background: 'linear-gradient(135deg, #F2F4F7 0%, #E7EBF0 100%)',
    border: '1px solid #D2D9E1',
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: 'currentColor',
  },
  loadingScreen: {
    position: 'fixed',
    top: 72,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    background: '#ffffff',
  },
  loadingLogoWrap: {
    width: 'min(680px, 62vw)',
    maxWidth: 'calc(100vw - 48px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [muiTheme.breakpoints.down('sm')]: {
      width: 'min(520px, 78vw)',
    },

    [muiTheme.breakpoints.down('xs')]: {
      width: 'min(390px, 86vw)',
    },
  },
  loadingLogo: {
    display: 'block',
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    background: 'transparent',
    filter: 'none',
  },
  errorCard: {
    maxWidth: 600,
    margin: '70px auto',
    padding: 28,
    borderRadius: 18,
    background: '#ffffff',
    border: '1px solid #efc9c4',
    boxShadow: '0 18px 48px rgba(26, 53, 80, 0.09)',
    textAlign: 'center',
  },
  errorTitle: {
    margin: 0,
    color: '#9b3e36',
    fontSize: 20,
    fontWeight: 800,
  },
  errorText: {
    margin: '9px 0 0',
    color: '#785a58',
    fontSize: 14,
    lineHeight: 1.55,
  },
  dialogPaper: {
    borderRadius: 16,
  },
  dialogTitle: {
    color: '#243b52',
    fontWeight: 800,
  },
  dangerButton: {
    color: '#ffffff',
    background: '#b9473d',

    '&:hover': {
      background: '#9e382f',
    },
  },
}));

const MRIList = () => {
  const classes = useStyles();
  const {
    token,
    authBody,
    setAuthenticated,
    setAuthBody,
  } = useContext(AuthorizeContext);

  const profile = authBody || { role: 'visitor' };
  const hasRole = (roles) => roles.includes(profile.role);

  const [state, setState] = useState({
    data: [],
    loading: true,
    error: '',
    dialogOpen: false,
    rowData: null,
    resetting: false,
  });

  const isAdminDirectory = hasRole(['super_admin', 'write_admin', 'read_admin', 'partner_admin']);
  const canEdit = hasRole(['super_admin']);

  const closeDialog = () => {
    if (state.resetting) {
      return;
    }

    setState((previous) => ({
      ...previous,
      dialogOpen: false,
      rowData: null,
    }));
  };

  const handleSessionExpired = () => {
    setAuthenticated(false);
    setAuthBody({ role: 'visitor' });
    alert('You have been logged out due to inactivity. Please log in again.');
  };

  useEffect(() => {
    let isMounted = true;

    const fetchDirectory = async () => {
      try {
        const response = await axios({
          url: `https://${api_url}/api/mri_center_list`,
          method: 'get',
          headers: {
            'x-access-token': token,
          },
        });

        if (!isMounted) {
          return;
        }

        if (response.data && response.data.message) {
          setState((previous) => ({
            ...previous,
            loading: false,
            error: response.data.message,
          }));
          return;
        }

        if (response.data && response.data.validation) {
          setState((previous) => ({
            ...previous,
            loading: false,
            error: response.data.validation.replace('_', ' '),
          }));
          return;
        }

        if (response.data && response.data.notLoggedIn) {
          handleSessionExpired();
          return;
        }

        setState((previous) => ({
          ...previous,
          loading: false,
          data: Array.isArray(response.data) ? response.data : [],
        }));
      } catch (requestError) {
        if (!isMounted) {
          return;
        }

        setState((previous) => ({
          ...previous,
          loading: false,
          error:
            (requestError.response
              && requestError.response.data
              && requestError.response.data.message)
            || requestError.message
            || 'Unable to load this directory.',
        }));
      }
    };

    fetchDirectory();

    return () => {
      isMounted = false;
    };
  }, [token, setAuthBody, setAuthenticated]);

  const availabilityColumn = useMemo(
    () => ({
      title: 'Availability',
      field: 'available',
      width: 90,
      type: 'boolean',
      filtering: true,
      sorting: true,
      render: (rowData) => {
        const available = Boolean(rowData.available);

        return (
          <span
            className={`${classes.statusBadge} ${
              available ? classes.statusActive : classes.statusInactive
            }`}
          >
            <span className={classes.statusDot} />
            {available ? 'Available' : 'Unavailable'}
          </span>
        );
      },
    }),
    [
      classes.statusActive,
      classes.statusBadge,
      classes.statusDot,
      classes.statusInactive,
    ]
  );

  const adminColumns = useMemo(
    () => [
      {
        title: 'Center Name',
        field: 'Office_Name',
        width: 112,
        render: (rowData) => (
          <div className={classes.officeCell}>
            {rowData.Office_Name || '—'}
          </div>
        ),
      },
      {
        title: 'First Name',
        field: 'First_Name',
        width: 65,
      },
      {
        title: 'Middle Name',
        field: 'Middle_Name',
        width: 50,
      },
      {
        title: 'Last Name',
        field: 'Last_Name',
        width: 82,
      },
      {
        title: 'Country',
        field: 'Country',
        width: 48,
      },
      {
        title: 'City',
        field: 'City',
        width: 70,
      },
      {
        title: 'Postal Code',
        field: 'Postal_Code',
        width: 62,
      },
      {
        title: 'Street',
        field: 'Street',
        width: 118,
        render: (rowData) => (
          <div className={classes.addressCell}>{rowData.Street || '—'}</div>
        ),
      },
      {
        title: 'State/Province',
        field: 'State_Province',
        width: 58,
      },
      {
        title: 'Email Address',
        field: 'Email_Address',
        width: 225,
        cellStyle: {
          minWidth: 170,
          whiteSpace: 'nowrap',
          overflow: 'visible',
          textOverflow: 'clip',
        },
        headerStyle: {
          minWidth: 170,
          whiteSpace: 'nowrap',
        },
        render: (rowData) => (
          <span
            className={classes.emailCell}
            title={rowData.Email_Address || ''}
          >
            {rowData.Email_Address || '—'}
          </span>
        ),
      },
      {
        title: 'Phone',
        field: 'Phone',
        width: 90,
      },
      {
        title: 'Fax',
        field: 'Fax',
        width: 68,
      },

      availabilityColumn,
    ],
    [availabilityColumn, classes]
  );

  const standardColumns = useMemo(
    () => [
      {
        title: 'Center Name',
        field: 'Office_Name',
        width: 112,
        editable: 'never',
        render: (rowData) => (
          <Link
            className={classes.nameLink}
            to={`/mricentercases/${rowData.Email_Address}`}
          >
            {rowData.Office_Name || '—'}
          </Link>
        ),
      },
      {
        title: 'First Name',
        field: 'First_Name',
        width: 65,
        editable: 'never',
      },
      {
        title: 'Middle Name',
        field: 'Middle_Name',
        width: 50,
        editable: 'never',
      },
      {
        title: 'Last Name',
        field: 'Last_Name',
        width: 82,
        editable: 'never',
      },
      {
        title: 'Office Address',
        field: 'Office_Address',
        width: 175,
        editable: 'never',
        render: (rowData) => (
          <div className={classes.addressCell}>
            {rowData.Street || '—'}
            <br />
            {[rowData.City, rowData.State_Province, rowData.Country]
              .filter(Boolean)
              .join(', ')}
          </div>
        ),
      },
      {
        title: 'Email Address',
        field: 'Email_Address',
        width: 225,
        editable: 'never',
        cellStyle: {
          minWidth: 170,
          whiteSpace: 'nowrap',
          overflow: 'visible',
          textOverflow: 'clip',
        },
        headerStyle: {
          minWidth: 170,
          whiteSpace: 'nowrap',
        },
        render: (rowData) => (
          <span
            className={classes.emailCell}
            title={rowData.Email_Address || ''}
          >
            {rowData.Email_Address || '—'}
          </span>
        ),
      },
      {
        title: 'Phone',
        field: 'Phone',
        width: 90,
        editable: 'never',
      },
      {
        title: 'Fax',
        field: 'Fax',
        width: 68,
        editable: 'never',
      },

      availabilityColumn,
    ],
    [availabilityColumn, classes]
  );

  const resetPassword = async () => {
    if (!state.rowData) {
      return;
    }

    setState((previous) => ({ ...previous, resetting: true }));

    try {
      const response = await axios({
        url: `https://${api_url}/api/reset_password?login=${encodeURIComponent(
          state.rowData.Email_Address
        )}`,
        method: 'post',
        headers: {
          'x-access-token': token,
        },
      });

      if (response.data && response.data.message) {
        alert(response.data.message);
        setState((previous) => ({ ...previous, resetting: false }));
        return;
      }

      if (response.data && response.data.validation) {
        alert(response.data.validation.replace('_', ' '));
        setState((previous) => ({ ...previous, resetting: false }));
        return;
      }

      if (response.data && response.data.notLoggedIn) {
        handleSessionExpired();
        return;
      }

      setState((previous) => ({
        ...previous,
        resetting: false,
        dialogOpen: false,
        rowData: null,
      }));
    } catch (requestError) {
      alert(
        (requestError.response
          && requestError.response.data
          && requestError.response.data.message)
        || requestError.message
        || 'Unable to reset the password.'
      );
      setState((previous) => ({ ...previous, resetting: false }));
    }
  };

  const updateRow = (newData, oldData) =>
    new Promise((resolve, reject) => {
      axios({
        url: `https://${api_url}/api/mri_center?email=${encodeURIComponent(
          oldData.Email_Address
        )}`,
        method: 'put',
        data: newData,
        headers: {
          'x-access-token': token,
        },
      })
        .then((response) => {
          if (response.data && response.data.message) {
            alert(response.data.message);
            reject();
            return;
          }

          if (response.data && response.data.validation) {
            alert(response.data.validation.replace('_', ' '));
            reject();
            return;
          }

          if (response.data && response.data.notLoggedIn) {
            handleSessionExpired();
            reject();
            return;
          }

          setState((previous) => {
            const updatedData = [...previous.data];
            const rowIndex = updatedData.indexOf(oldData);

            if (rowIndex >= 0) {
              updatedData[rowIndex] = newData;
            }

            return {
              ...previous,
              data: updatedData,
            };
          });

          resolve();
        })
        .catch((requestError) => {
          alert(
            (requestError.response
              && requestError.response.data
              && requestError.response.data.message)
            || requestError.message
            || 'Unable to save this record.'
          );
          reject();
        });
    });

  if (state.loading) {
    return (
      <div className={classes.loadingScreen}>
        <div className={classes.loadingLogoWrap}>
          <img
            className={classes.loadingLogo}
            src={logo}
            alt="Lento Medical Innovation"
          />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <main className={classes.page}>
        <div className={classes.errorCard}>
          <h2 className={classes.errorTitle}>Directory unavailable</h2>
          <p className={classes.errorText}>{state.error}</p>
        </div>
      </main>
    );
  }

  const pageTitle = 'MRI Center Directory';
  const pageSubtitle = '';

  return (
    <ThemeProvider theme={theme}>
      <main className={classes.page}>
        <div className={classes.shell}>
          <header className={classes.pageHeader}>
            <div>
              <p className={classes.eyebrow}>Organization directory</p>
              <h1 className={classes.title}>{pageTitle}</h1>
              <p className={classes.subtitle}>{pageSubtitle}</p>
            </div>

            <div className={classes.headerMeta}>
              <span className={classes.metaPill}>
                <span className={classes.metaDot} />
                {state.data.length} {state.data.length === 1 ? 'record' : 'records'}
              </span>
            </div>
          </header>

          <section className={classes.card}>
            <div className={classes.cardIntro}>
              <div>
                <p className={classes.cardLabel}>Directory records</p>
                <p className={classes.cardHint}>
                  Search, sort, filter, and review account details from one place.
                </p>
              </div>


            </div>

            <MaterialTable
              title=""
              columns={isAdminDirectory ? adminColumns : standardColumns}
              data={state.data}
              icons={tableIcons}
              actions={
                canEdit
                  ? [
                      {
                        icon: tableIcons.Reset,
                        tooltip: 'Reset password',
                        onClick: (event, rowData) => {
                          setState((previous) => ({
                            ...previous,
                            dialogOpen: true,
                            rowData,
                          }));
                        },
                      },
                    ]
                  : []
              }
              editable={
                canEdit
                  ? {
                      onRowUpdate: updateRow,
                    }
                  : undefined
              }
              localization={{
                pagination: {
                  labelDisplayedRows: '{from}-{to} of {count}',
                  labelRowsSelect: 'rows',
                  firstAriaLabel: 'First page',
                  firstTooltip: 'First page',
                  previousAriaLabel: 'Previous page',
                  previousTooltip: 'Previous page',
                  nextAriaLabel: 'Next page',
                  nextTooltip: 'Next page',
                  lastAriaLabel: 'Last page',
                  lastTooltip: 'Last page',
                },
                toolbar: {
                  nRowsSelected: '{0} row(s) selected',
                  searchPlaceholder: 'Search directory...',
                  searchTooltip: 'Search directory',
                },
                header: {
                  actions: 'Actions',
                },
                body: {
                  emptyDataSourceMessage: 'No records found',
                  editRow: {
                    deleteText: 'Are you sure you want to delete this record?',
                    cancelTooltip: 'Cancel',
                    saveTooltip: 'Save changes',
                  },
                  filterRow: {
                    filterTooltip: 'Filter',
                  },
                },
              }}
              options={{
                actionsColumnIndex: -1,
                actionsCellStyle: {
                  paddingLeft: 2,
                  paddingRight: 2,
                  textAlign: 'center',
                },
                columnsButton: true,
                debounceInterval: 300,
                draggable: false,
                emptyRowsWhenPaging: false,
                filtering: true,
                pageSize: 15,
                pageSizeOptions: [10, 15, 25, 50],
                paging: true,
                rowStyle: (rowData, index) => ({
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#fbfcfe',
                }),
                search: true,
                searchFieldAlignment: 'right',
                searchFieldStyle: {
                  width: 280,
                  maxWidth: '36vw',
                },
                showFirstLastPageButtons: true,
                showTitle: false,
                sorting: true,
                tableLayout: 'auto',
                toolbar: true,
              }}
              style={{
                background: '#ffffff',
                boxShadow: 'none',
              }}
            />
          </section>
        </div>
      </main>

      <Dialog
        open={state.dialogOpen}
        onClose={closeDialog}
        aria-labelledby="reset-password-dialog-title"
        PaperProps={{ className: classes.dialogPaper }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          id="reset-password-dialog-title"
          className={classes.dialogTitle}
        >
          Reset password
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Reset the password for{' '}
            {state.rowData ? state.rowData.Office_Name : 'this MRI center'}?
            The office will need to use the newly reset credentials the next
            time it signs in.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeDialog}
            variant="outlined"
            disabled={state.resetting}
          >
            Cancel
          </Button>
          <Button
            onClick={resetPassword}
            className={classes.dangerButton}
            variant="contained"
            disabled={state.resetting}
          >
            {state.resetting ? 'Resetting...' : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default MRIList;